import { postMergeCart } from '@/api/postMergeCart';
import type { LocalCartItem } from '@/types';
import { pickErrorMessages } from '@/utils/functions';

const CART_MERGED_KEY = 'cartMerged';
// マージ失敗の保留フラグ。タブを閉じても残るよう localStorage に置く。
// （sessionStorage だと「障害中にブラウザを閉じる → 翌日アクセス」で救済が消滅する）
const CART_MERGE_PENDING_KEY = 'cartMergePending';

/**
 * ローカルカートをサーバーにマージし、既存アイテムが追加された場合は通知フラグを立てる
 * @returns カートマージで件数が増えた場合 true
 */
export const mergeCartWithNotification = async (
  items: LocalCartItem[],
  clearCartInfo: () => void,
): Promise<boolean> => {
  const localItemCount = items.length;
  const data = items.map(({ userPrintId, quantity, timestamp, ...route }) => ({
    userPrintId,
    quantity,
    timestamp,
    ...route,
  }));

  const mergeResult = await postMergeCart({ items: data });
  clearCartInfo();
  clearCartMergePending();

  // マージ後のサーバーカート件数を取得し、ローカルより増えていたら通知フラグを立てる
  // ログイン先のアカウントでカートにグッズがあった場合に
  // マージが発生して意図しない個数の商品を買うトラブルを防止する
  if (mergeResult.result.cart.totalItems > localItemCount) {
    try {
      sessionStorage.setItem(CART_MERGED_KEY, 'true');
    } catch (error) {
      pickErrorMessages(error);
    }
    return true;
  }
  return false;
};

/**
 * カートマージ通知フラグを消費して返す
 */
export const consumeCartMergedFlag = (): boolean => {
  try {
    if (sessionStorage.getItem(CART_MERGED_KEY)) {
      sessionStorage.removeItem(CART_MERGED_KEY);
      return true;
    }
  } catch (error) {
    pickErrorMessages(error);
  }
  return false;
};

/**
 * マージ恒久失敗時の救済フラグを立てる。
 * GfCheckCart が次回以降のページアクセス時にこのフラグを見て再試行する。
 */
export const setCartMergePending = (): void => {
  try {
    localStorage.setItem(CART_MERGE_PENDING_KEY, 'true');
  } catch (error) {
    pickErrorMessages(error);
  }
};

/**
 * マージ保留フラグが立っているか
 */
export const hasCartMergePending = (): boolean => {
  try {
    return localStorage.getItem(CART_MERGE_PENDING_KEY) === 'true';
  } catch (error) {
    pickErrorMessages(error);
    return false;
  }
};

/**
 * マージ保留フラグをクリアする
 */
export const clearCartMergePending = (): void => {
  try {
    localStorage.removeItem(CART_MERGE_PENDING_KEY);
  } catch (error) {
    pickErrorMessages(error);
  }
};
